"use client";

import type { Locale } from "@/lang";
import { homePath, isHomePath } from "@/lib/locale-routing";

export type Profile = "Individual" | "Organization";

export type ProfileNavDetail = {
  profile: Profile;
  scrollTo: string;
  keepProfile?: boolean;
};

const PENDING_NAV_KEY = "flots:pending-nav";

type Router = { push: (href: string) => void };

function fireOrQueue(
  detail: ProfileNavDetail,
  currentRoute: string,
  router: Router,
  locale: Locale
) {
  // The homepage exists at two URLs ("/" and "/en") — HomeClient is mounted
  // on both, so dispatch directly from either.
  if (isHomePath(currentRoute)) {
    window.dispatchEvent(new CustomEvent("flots:set-profile", { detail }));
    return;
  }
  // Home isn't mounted from here, so queue the intent and let HomeClient
  // pick it up (via consumePendingNav) once it mounts after navigation.
  // Navigate to the home URL of the CURRENT language — pushing "/" from an
  // /en page would silently flip the whole site back to French.
  sessionStorage.setItem(PENDING_NAV_KEY, JSON.stringify(detail));
  router.push(homePath(locale));
}

/** Jump to a feature section, switching the active profile (Individual/Organization). */
export function navigateToProfileSection(
  currentRoute: string,
  router: Router,
  profile: Profile,
  scrollTo: string,
  locale: Locale = "fr"
) {
  fireOrQueue({ profile, scrollTo }, currentRoute, router, locale);
}

/** Jump to a shared section (pricing, contact) without changing the active profile. */
export function navigateToSection(
  currentRoute: string,
  router: Router,
  profile: Profile,
  scrollTo: string,
  locale: Locale = "fr"
) {
  fireOrQueue({ profile, scrollTo, keepProfile: true }, currentRoute, router, locale);
}

/** Read and clear any nav intent queued before navigating to the home page. */
export function consumePendingNav(): ProfileNavDetail | null {
  const raw = sessionStorage.getItem(PENDING_NAV_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(PENDING_NAV_KEY);
  try {
    return JSON.parse(raw) as ProfileNavDetail;
  } catch {
    return null;
  }
}
